import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthGate } from './auth/AuthGate';
import { ToastProvider } from './ui/toast';
import { TimelinePage } from './pages/TimelinePage';
import { NoteListPage } from './pages/NoteListPage';
import { loadViewName, viewPath } from './views/view-preference';
import { NotePage } from './pages/NotePage';
import { NoteViewPage } from './pages/NoteViewPage';
import { SearchPage } from './pages/SearchPage';
import { TrashPage } from './pages/TrashPage';
import { SettingsPage } from './pages/SettingsPage';
import { RevisionsPage } from './pages/RevisionsPage';

// IME 検証ページ（docs/03-editor.md §7）。
//
// 本番ビルドにも含める。段階0の DoD である Android 実機での IME 検証は、
// スマートフォンから開発サーバーへ到達できない以上、本番 URL でしか行えないため。
// 認証の内側にあり、単独ユーザーのアプリなので露出の問題はない。
// 起動経路に載せないよう、遅延読み込みで別チャンクに分ける。
const ImeTestPage = lazy(async () => {
  const mod = await import('./dev/ImeTestPage');
  return { default: mod.ImeTestPage };
});

/**
 * 起動時に、最後に選んだビューへ転送する（docs/01-requirements.md §5.1）。
 * `replace` にするのは、履歴に `/` を積むと戻るボタンが転送を繰り返してループするため。
 */
function ViewEntry() {
  return <Navigate to={viewPath(loadViewName())} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthGate>
          <Routes>
            {/* `/` は記憶したビューへの転送口（PWA の start_url でもある）。
                ビューの実体は /timeline と /list（ADR-030）。 */}
            <Route path="/" element={<ViewEntry />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/list" element={<NoteListPage />} />
            {/* /notes/new は「起動〜入力開始」を最短にするための入口。
                サーバーへの POST は初回自動保存まで遅延させる（docs/01 §5.1）。 */}
            <Route path="/notes/new" element={<NotePage />} />
            <Route path="/notes/:noteId" element={<NoteViewPage />} />
            <Route path="/notes/:noteId/edit" element={<NotePage />} />
            <Route path="/notes/:noteId/revisions" element={<RevisionsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/trash" element={<TrashPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route
              path="/dev/ime-test"
              element={
                <Suspense fallback={<div className="centered-message">読み込み中…</div>}>
                  <ImeTestPage />
                </Suspense>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthGate>
      </ToastProvider>
    </BrowserRouter>
  );
}
